import React, { useMemo } from 'react';
import {
  POSSIBLE_ALARMS,
  ZoneResponse,
} from '../../../../service/store/modules/zone/ZoneService.model';
import { ReactComponent as PowerIcon } from '../../../../assets/power.svg';
import { ReactComponent as LightIcon } from '../../../../assets/light.svg';
import { ReactComponent as ErrorIcon } from '../../../../assets/error.svg';
import { ReactComponent as DisconnectedIcon } from '../../../../assets/disconnected.svg';
import Button, { ButtonProps } from '../Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { useTheme, Theme } from '@mui/material/styles';
import { CircularProgress } from '@mui/material';
import {
  ErrorDoorOpenIcon,
  TempNotReachedIcon,
  TempTooHighIcon,
  TempTooLowIcon,
} from '../../../../assets';
import {
  TEMPERATURE_UNIT_TYPES,
  Temperature,
  useTemperaturePreference,
} from '../../../../service/temperaturePreference';
import { ZONE } from '../../../../service/store/hooks/useZoneDevice';

export type ZoneButtonProps = Omit<ButtonProps, 'variant'> & {
  zone: ZoneResponse;
  alert?: boolean;
  disconnected?: boolean;
  loading?: boolean;
  position?: ZONE;
};

export function getBackgroundColor(
  theme: Theme,
  disconnected?: boolean,
  error?: boolean,
  isOff?: boolean,
  alert?: boolean
) {
  if (disconnected) return '#303030';
  if (error) return '#E50E0E';
  if (isOff) return undefined;
  if (alert) return '#E50E0E';
  return theme.palette.mode === 'dark' ? '#EAEAEB' : '#FFF';
}

function getErrorIcon(zone: ZoneResponse) {
  const alarms = zone.shelfStatus.alarms ?? [];
  let firstCompatibleAlarm: POSSIBLE_ALARMS | 'DOOR_OPEN_ERROR' | undefined =
    alarms.find(
      (alarm) =>
        alarm === POSSIBLE_ALARMS.OVERTEMP ||
        alarm === POSSIBLE_ALARMS.UNDERTEMP ||
        alarm === POSSIBLE_ALARMS.SETPOINT_NOT_REACHED
    );
  // Add error case door open
  if (!firstCompatibleAlarm && zone.shelfStatus.doorState === 'ERROR') {
    firstCompatibleAlarm = 'DOOR_OPEN_ERROR';
  }

  switch (firstCompatibleAlarm) {
    case POSSIBLE_ALARMS.OVERTEMP:
      return {
        icon: <TempTooHighIcon height={35} />,
        text: 'timers.error_temperature_overtemp',
      };
    case POSSIBLE_ALARMS.UNDERTEMP:
      return {
        icon: <TempTooLowIcon height={35} />,
        text: 'timers.error_temperature_undertemp',
      };
    case POSSIBLE_ALARMS.SETPOINT_NOT_REACHED:
      return {
        icon: <TempNotReachedIcon height={35} />,
        text: 'timers.error_temperature_SetpointNotReached',
      };
    case 'DOOR_OPEN_ERROR':
      return {
        icon: <ErrorDoorOpenIcon height={35} />,
        text: 'timers.error_door_open',
      };
    default:
      return { icon: <ErrorIcon height={35} />, text: 'timers.error' };
  }
}
const getBackgroundColorFromZone = (props: ZoneButtonProps, theme: Theme) => {
  const { zone, alert, disconnected } = props;
  const isOff =
    zone.activeSetting === 'OFF' || zone.shelfStatus.deviceOn === false;
  const {
    shelfStatus: { operatingMode },
  } = zone;
  const error =
    operatingMode === 'ERROR' || operatingMode === 'ERROR_CU_MISMATCH';
  return getBackgroundColor(theme, disconnected, error, isOff, alert);
};

export function getBackgroundColorHover(
  theme: Theme,
  disconnected?: boolean,
  error?: boolean,
  isOff?: boolean,
  alert?: boolean
) {
  if (disconnected) return '#303030';
  if (error) return 'error.main';
  if (isOff) return undefined;
  if (alert) return '#CE0F0F';
  return theme.palette.mode === 'dark' ? '#DEDEDE' : '#DDD';
}
const getBackgroundColorHoverFromZone = (
  props: ZoneButtonProps,
  theme: Theme
) => {
  const { zone, alert, disconnected } = props;
  const isOff =
    zone.activeSetting === 'OFF' || zone.shelfStatus.deviceOn === false;
  const {
    shelfStatus: { operatingMode },
  } = zone;
  const error =
    operatingMode === 'ERROR' || operatingMode === 'ERROR_CU_MISMATCH';
  return getBackgroundColorHover(theme, disconnected, error, isOff, alert);
};

const getHeight = (props: ZoneButtonProps, theme: Theme) => {
  switch (props.size) {
    case 'small':
      return 90 / 2;
    case 'large':
      return 90 * 2;
    default:
      return 90;
  }
};

const getWidth = (props: ZoneButtonProps, theme: Theme) => {
  switch (props.size) {
    case 'small':
      return 130 / 2;
    case 'large':
      return 130 * 2;
    default:
      return 130;
  }
};

const ZoneButton = React.forwardRef(function ButtonRef(
  inProps: ZoneButtonProps,
  ref: React.Ref<HTMLButtonElement>
) {
  const {
    zone: { activeSetting, shelfStatus },
    disconnected: disconnectedCommander,
    sx,
    loading,
    ...props
  } = inProps;

  const error =
    shelfStatus.operatingMode === 'ERROR' ||
    shelfStatus.operatingMode === 'ERROR_CU_MISMATCH';
  const disconnected = useMemo(
    () =>
      Boolean(
        error &&
          shelfStatus.alarms?.find((alarm) => alarm === POSSIBLE_ALARMS.DISCONN)
      ) || disconnectedCommander,
    [error, shelfStatus.alarms, disconnectedCommander]
  );
  const inferProps = { ...inProps, disconnected };

  const theme = useTheme();
  const isOff = activeSetting === 'OFF' || shelfStatus.deviceOn === false;
  const variant = isOff && !disconnected ? 'contained' : 'contained-gray';

  return (
    <Button
      {...props}
      ref={ref}
      variant={variant}
      disabled={disconnected || props.disabled}
      sx={{
        height: getHeight(inferProps, theme),
        width: getWidth(inferProps, theme),
        borderRadius: 3,
        padding: error ? 0.5 : props.size === 'small' ? 0.5 : 1.5,
        backgroundColor: getBackgroundColorFromZone(inferProps, theme),
        position: 'relative',
        borderWidth: theme.palette.mode === 'dark' ? 0 : 1,
        borderStyle: 'solid',
        '&:hover': {
          backgroundColor: getBackgroundColorHoverFromZone(inferProps, theme),
        },
        ...sx,
      }}
    >
      <ZoneButtonContent {...inferProps} />
      {loading && (
        <Box
          sx={{
            position: 'absolute',
            top: theme.spacing(props.size === 'small' ? 0.5 : 1.5),
            left: theme.spacing(props.size === 'small' ? 0.5 : 2.5),
            color: 'white',
          }}
        >
          <CircularProgress size={15} color={isOff ? 'inherit' : 'primary'} />
        </Box>
      )}
    </Button>
  );
});

const ZoneButtonContent = (props: ZoneButtonProps): JSX.Element => {
  const { zone, disconnected, alert, loading, position } = props;
  const {
    activeSetting,
    shelfStatus: { deviceOn, operatingMode, temperatureSetpoint, alarms },
  } = zone;
  const { t } = useTranslation();
  const theme = useTheme();
  const height = getHeight(props, theme);
  const warning = operatingMode === 'TRANSITIONING';
  const error =
    operatingMode === 'ERROR' || operatingMode === 'ERROR_CU_MISMATCH';
  const isOff = activeSetting === 'OFF' || deviceOn === false;
  const isLightOnly = activeSetting === 'LIGHTS_ONLY';
  const temperaturePreference = useTemperaturePreference();
  const temperaturePreferenceLabel = t(
    `settings.temperature_preference.${temperaturePreference}`
  );
  const setpointValue = temperatureSetpoint;

  let bodyText = `${
    temperaturePreference === TEMPERATURE_UNIT_TYPES.FAHRENHEIT
      ? Temperature.convertCelsiusToFahrenheitRound(setpointValue)
      : Temperature.round(setpointValue)
  }`;

  let fontSize = height / 2;
  let lineHeight = 1;
  const isAuxiliaryDevice = position && position === ZONE.LOWER_ZONE;

  if (disconnected)
    return (
      <Box
        sx={{
          width: '100%',
          color: 'darkgray',
          stroke: 'white',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
            marginX: 1,
          }}
        >
          <Circle disconnected={disconnected} warning={warning} />
          <Typography
            component={'span'}
            lineHeight={1}
            fontSize={18}
            fontWeight={600}
          ></Typography>
        </Box>
        <DisconnectedIcon height={25} />

        <Typography lineHeight={1} fontWeight={600}>
          {t('timers.disconnected')}
        </Typography>
      </Box>
    );
  if (error || alarms.length) {
    const { icon, text } = getErrorIcon(zone);
    return (
      <Box
        sx={(theme) => ({
          width: '100%',
          height: '100%',
          color: theme.palette.error.main,
          stroke: theme.palette.error.main,
          backgroundColor: '#F59F9F',
          borderRadius: 3,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
        })}
      >
        {icon}
        <Typography
          lineHeight={1}
          fontWeight={600}
          marginTop={1}
          textTransform="uppercase"
          fontSize={'0.75em'}
        >
          {t(text)}
        </Typography>
      </Box>
    );
  }
  /** Impossible case, zone unused are hidden */
  if (operatingMode === 'UNUSED') {
    return <>UNUSED</>;
  }

  if (isOff) {
    return <PowerIcon height={height / 1.5} />;
  }

  if (isLightOnly) {
    return <LightIcon height={height / 1.5} />;
  }

  if (isAuxiliaryDevice) {
    bodyText = t('general.on').toUpperCase();
    fontSize = bodyText.length > 3 ? fontSize / 2.5 : fontSize;
    lineHeight = bodyText.length > 3 ? 2 : 1;
  }

  return (
    <Box
      sx={[
        {
          width: '100%',
        },
        alert
          ? {
              color: 'white',
            }
          : {
              color: 'black',
            },
      ]}
    >
      <Box
        sx={[
          {
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
          },
          props.size === 'small'
            ? {
                paddingX: 0,
              }
            : {
                paddingX: 1,
              },
        ]}
      >
        {loading ? (
          <div></div>
        ) : (
          <Circle disconnected={disconnected} warning={warning} />
        )}

        {!isAuxiliaryDevice && (
          <Typography
            component={'span'}
            lineHeight={1}
            fontSize={fontSize / 2}
            fontWeight={600}
          >
            {temperaturePreferenceLabel}
          </Typography>
        )}
      </Box>
      <Typography fontSize={fontSize} lineHeight={lineHeight} fontWeight={600}>
        {bodyText}
      </Typography>
    </Box>
  );
};

const Circle = ({
  disconnected,
  warning,
}: {
  warning?: boolean;
  disconnected?: boolean;
}) => {
  const { t } = useTranslation();
  return (
    <Box
      className="Zone-status"
      title={`${t('timers.status')} ${
        disconnected
          ? t('timers.disconnected')
          : warning
          ? t('timers.status_warning')
          : t('timers.status_ok')
      }`}
      sx={(theme) => ({
        borderRadius: '10px',
        backgroundColor: 'white',
        height: theme.spacing(2.2),
        width: theme.spacing(2.2),
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        boxShadow: 'rgba(0, 0, 0, 0.4) 0 3px 4px 0;',
      })}
    >
      <Box
        className="Zone-status"
        sx={(theme) => ({
          borderRadius: '10px',
          backgroundColor: disconnected
            ? 'gray'
            : warning
            ? 'warning.main'
            : 'success.main',
          height: theme.spacing(1.8),
          width: theme.spacing(1.8),
        })}
      ></Box>
    </Box>
  );
};

export default ZoneButton;
